import {
  Currency,
  currencyCodes,
  fromUnitQuantityIntoBaseUnitQuantity,
  getAmountPerBaseUnit,
  ImpactType,
  User,
} from "@ecologi/shared";

const donationTypes = ["TREES", "BOTH", "CUSTOM"] as const;

export type DonationType = (typeof donationTypes)[number];

type DonationItemCost = {
  numberOfTrees: number;
  numberOfTonnes?: number;
};

type DonationOptions = Record<
  DonationType,
  Record<Currency, Record<string, DonationItemCost>>
>;

const getItemUnitCurrencyValueMap = (
  user?: User | null,
  isOwnProfile?: boolean
): Record<Currency, { trees: number; carbon: number }> =>
  currencyCodes.reduce(
    (map, currency) => (
      (map[currency] = {
        trees: getAmountPerBaseUnit({
          type: ImpactType.TREES,
          currency,
          amountPerUnit: "use-latest",
          isEdenPriceMatched:
            (user?.isEdenPriceMatchEnabled && isOwnProfile) === true,
        }).toNumber(),
        carbon: getAmountPerBaseUnit({
          type: ImpactType.CARBON_OFFSETS,
          amountPerUnit: "use-latest",
          currency,
        })
          .times(fromUnitQuantityIntoBaseUnitQuantity(1, "tonnes"))
          .toNumber(),
      }),
      map
    ),
    {} as Record<Currency, { trees: number; carbon: number }>
  );

const calculatePricesForCurrency = (
  currency: Currency,
  items: Array<[number, number?]>,
  user?: User | null,
  isOwnProfile?: boolean
): Record<string, DonationItemCost> =>
  items.reduce((priceMap, [trees, carbon]) => {
    const itemValueMap = getItemUnitCurrencyValueMap(user, isOwnProfile);
    const carbonPrice = (carbon || 0) * itemValueMap[currency].carbon;
    const treePrice = (trees || 0) * itemValueMap[currency].trees;

    priceMap[(carbonPrice + treePrice).toString()] = {
      numberOfTrees: trees,
      numberOfTonnes: carbon || undefined,
    };

    return priceMap;
  }, {});

export const getDonationImpactMapper = (
  user?: User | null,
  isOwnProfile?: boolean
): DonationOptions => {
  const tabItemQuantityValueMap: Record<
    DonationType,
    Array<[number, number?]>
  > = {
    BOTH: [
      [25, 1],
      [50, 1],
      [100, 2],
      [250, 5],
      [500, 10],
      [1000, 20],
      [5000, 100],
      [10000, 200],
    ],
    TREES: [[25], [50], [100], [250], [500], [1000], [5000], [10000]],
    CUSTOM: [],
  };

  return donationTypes.reduce(
    (options, type) => (
      (options[type] = currencyCodes.reduce(
        (currencyMap, currency) => (
          (currencyMap[currency] = calculatePricesForCurrency(
            currency,
            tabItemQuantityValueMap[type],
            user,
            isOwnProfile
          )),
          currencyMap
        ),

        {} as Record<Currency, Record<string, DonationItemCost>>
      )),
      options
    ),
    {} as DonationOptions
  );
};

// bespoke: Lapland have custom pricing and options for donations
export const donationImpactMapperLapland: Partial<DonationOptions> = {
  TREES: {
    ...getDonationImpactMapper().TREES,
    GBP: {
      "500": { numberOfTrees: 45 },
      "1000": { numberOfTrees: 90 },
      "2500": { numberOfTrees: 225 },
      "5000": { numberOfTrees: 450 },
      "10000": { numberOfTrees: 900 },
      "50000": { numberOfTrees: 4500 },
      "100000": { numberOfTrees: 9000 },
    },
  },
};
